// Login form.
import React from 'react';
import { FormattedMessage } from 'react-intl';

import CheckBox from '../widgets/checkbox.jsx';
import VisiblePassword from '../widgets/visible-password.jsx';

// babel: the original username + password form, kept for development and the test users (open the app with ?classic=1)
class PasswordLoginView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      login: props.login,
      password: '',
      hostName: props.serverAddress,
      saveToken: props.persist
    };
    this.handleLoginChange = this.handleLoginChange.bind(this);
    this.handlePasswordChange = this.handlePasswordChange.bind(this);
    this.handleToggleSaveToken = this.handleToggleSaveToken.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleLoginChange(e) {
    this.setState({login: e.target.value});
  }

  handlePasswordChange(e) {
    this.setState({password: e.target.value});
  }

  handleToggleSaveToken() {
    this.props.onPersistenceChange(!this.state.saveToken);
    this.setState({saveToken: !this.state.saveToken});
  }

  handleSubmit(e) {
    e.preventDefault();
    this.props.onLogin(this.state.login.trim(), this.state.password.trim());
  }

  render() {
    let submitClasses = 'primary';
    if (this.props.disabled) {
      submitClasses += ' disabled';
    }

    return (
      <form id="login-form" onSubmit={this.handleSubmit}>
        <FormattedMessage id="login_prompt" defaultMessage="Login"
          description="Placeholer for username/login">
        {
          (login_prompt) => <input type="text" id="inputLogin"
            placeholder={'Phone number (or username)'} // babel
            autoComplete="username"
            autoCorrect="off"
            autoCapitalize="none"
            value={this.state.login}
            onChange={this.handleLoginChange}
            required autoFocus />
        }
        </FormattedMessage>
        <FormattedMessage id="password_prompt" defaultMessage="Password"
          description="Placeholder/prompt for entering password">
        {
          (password_prompt) => <VisiblePassword type="password" id="inputPassword"
            placeholder={'Code (or password)'} // babel
            autoComplete="current-password"
            value={this.state.password}
            onChange={this.handlePasswordChange}
            required={true} />
        }
        </FormattedMessage>
        <div className="panel-form-row">
          <CheckBox id="save-token" name="save-token" checked={this.state.saveToken}
            onChange={this.handleToggleSaveToken} />
          <label htmlFor="save-token">&nbsp;
            <FormattedMessage id="stay_logged_in" defaultMessage="Stay logged in"
              description="Label for a checkbox" />
          </label>
          <a href="#reset">
            <FormattedMessage id="forgot_password_link" defaultMessage="Forgot password?"
              description="Link to Reset password form" />
          </a>
        </div>
        <div className="dialog-buttons">
          <button className={submitClasses} type="submit">
            <FormattedMessage id="button_sign_in" defaultMessage="Sign in"
              description="Button [Sign In]" />
          </button>
        </div>
      </form>
    );
  }
};

// babel: sign-in like an SMS login. Phone number -> "Send code" -> six digits -> in. The server call that "sends" the code is
// POST babel/v1/auth/code (a stub today, a real SMS later); the code itself is checked by Tinode's rest authenticator.
const COUNTRIES = [ // [dial code, short name]
  ['+972', 'Israel'], ['+1', 'US/Canada'], ['+44', 'UK'], ['+7', 'Russia/Kaz.'],
  ['+380', 'Ukraine'], ['+40', 'Romania'], ['+63', 'Philippines'], ['+66', 'Thailand'], ['+91', 'India'],
  ['+251', 'Ethiopia'], ['+49', 'Germany'], ['+33', 'France'], ['+34', 'Spain'], ['+39', 'Italy'], ['+90', 'Turkey'],
  ['+20', 'Egypt'], ['+962', 'Jordan'], ['+971', 'UAE'], ['+995', 'Georgia'], ['+374', 'Armenia']
];
const REGION_DIAL = {IL: '+972', US: '+1', CA: '+1', GB: '+44', RU: '+7', KZ: '+7', UA: '+380', RO: '+40', PH: '+63', TH: '+66',
  IN: '+91', ET: '+251', DE: '+49', FR: '+33', ES: '+34', IT: '+39', TR: '+90', EG: '+20', JO: '+962', AE: '+971', GE: '+995', AM: '+374'};
const MAX_WRONG = 5;           // wrong codes before this screen stops asking (the server locks the phone on its own too)
const LOCK_MS = 15 * 60 * 1000;

// The country calling code that fits the phone's region (from its locale), Israel when unknown.
function defaultDialCode() {
  const region = ((navigator.language || '').split('-')[1] || '').toUpperCase();
  return REGION_DIAL[region] || '+972';
}

// What the person typed -> +CCNNNN. A leading + or 00 is taken as is, a leading 0 is the national prefix.
export function toInternational(dial, typed) {
  const raw = (typed || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) { return '+' + digits; }
  if (digits.startsWith('00')) { return '+' + digits.slice(2); }
  return dial + digits.replace(/^0+/, '');
}

function triesKey(phone) { return 'babel-code-tries:' + phone; }
function readTries(phone) {
  try { return JSON.parse(localStorage.getItem(triesKey(phone))) || {n: 0, until: 0}; } catch (e) { return {n: 0, until: 0}; }
}
function writeTries(phone, v) {
  try { localStorage.setItem(triesKey(phone), JSON.stringify(v)); } catch (e) { /* private mode: the server still limits */ }
}

class PhoneLoginView extends React.Component {
  constructor(props) {
    super(props);
    const typed = /^\+?\d/.test(props.login || '') ? props.login : '';
    this.state = {
      step: 'phone', dial: defaultDialCode(), typed: typed.startsWith('+') ? typed : '', phone: '',
      code: '', error: '', info: '', busy: false, resendLeft: 0, locked: 0, saveToken: props.persist
    };
    this.tick = null;
    this.handleFailed = this.handleFailed.bind(this);
    this.sendCode = this.sendCode.bind(this);
    this.handleCodeChange = this.handleCodeChange.bind(this);
    this.handleToggleSaveToken = this.handleToggleSaveToken.bind(this);
  }

  componentDidMount() {
    window.addEventListener('babel-login-failed', this.handleFailed);
    this.tick = setInterval(() => {
      if (this.state.resendLeft > 0) { this.setState({resendLeft: this.state.resendLeft - 1}); }
      if (this.state.locked && Date.now() >= this.state.locked) { this.setState({locked: 0, error: ''}); }
    }, 1000);
  }

  componentWillUnmount() {
    window.removeEventListener('babel-login-failed', this.handleFailed);
    clearInterval(this.tick);
  }

  handleToggleSaveToken() {
    this.props.onPersistenceChange(!this.state.saveToken);
    this.setState({saveToken: !this.state.saveToken});
  }

  // The sign-in was refused. A wrong code and a locked phone look the same to us (so nobody can learn who is a member),
  // so this screen counts its own wrong tries.
  handleFailed(e) {
    if (this.state.step !== 'code' || !e.detail || e.detail.code !== 401) { return; }
    const t = readTries(this.state.phone);
    t.n += 1;
    if (t.n >= MAX_WRONG) { t.until = Date.now() + LOCK_MS; t.n = 0; }
    writeTries(this.state.phone, t);
    this.setState(t.until > Date.now() ?
      {code: '', locked: t.until, error: 'Too many wrong codes. Try again in 15 minutes.'} :
      {code: '', error: 'That code is not right. Check it and try again.'});
  }

  async sendCode(e, resend) {
    if (e) { e.preventDefault(); }
    const phone = toInternational(this.state.dial, this.state.typed);
    if (!/^\+\d{8,15}$/.test(phone)) { this.setState({error: 'Enter a valid phone number.'}); return; }
    const t = readTries(phone);
    if (t.until > Date.now()) {
      this.setState({step: 'code', phone: phone, code: '', locked: t.until, error: 'Too many wrong codes. Try again in 15 minutes.'});
      return;
    }
    this.setState({busy: true, error: '', info: ''});
    try {
      const resp = await fetch('babel/v1/auth/code', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({phone: phone})});
      const body = await resp.json().catch(_ => ({}));
      if (resp.ok) {
        this.setState({step: 'code', phone: phone, code: '', busy: false, resendLeft: body.resend_after || 30, locked: 0,
          info: resend ? 'A new code was requested.' : ''});
      } else if (resp.status === 429 && body.error === 'too_soon') {
        // one second of margin: the server counts whole seconds, so a click right at zero could still be too early
        this.setState({step: 'code', phone: phone, code: '', busy: false, resendLeft: (body.retry_after || 30) + 1});
      } else if (resp.status === 429) {
        const min = Math.max(1, Math.ceil((body.retry_after || 600) / 60));
        this.setState({busy: false, error: 'Too many requests. Try again in ' + min + (min === 1 ? ' minute.' : ' minutes.')});
      } else {
        this.setState({busy: false, error: resp.status === 400 ? 'Enter a valid phone number.' : 'Something went wrong. Try again.'});
      }
    } catch (err) {
      this.setState({busy: false, error: 'Cannot reach the server. Check your connection.'});
    }
  }

  handleCodeChange(e) {
    const code = e.target.value.replace(/\D/g, '').slice(0, 6);
    this.setState({code: code, error: ''});
    if (code.length === 6 && !this.props.disabled) {
      this.props.onLogin(this.state.phone, code);
    }
  }

  render() {
    const s = this.state;
    const mmss = (n) => Math.floor(n / 60) + ':' + String(n % 60).padStart(2, '0');
    if (s.step === 'phone') {
      return (
        <form id="login-form" className="babel-login" onSubmit={this.sendCode}>
          <div className="babel-login-lead">Enter your phone number. We will ask for the code you were given.</div>
          <div className="babel-login-phone">
            <select id="inputDial" value={s.dial} onChange={e => this.setState({dial: e.target.value})} aria-label="Country">
              {COUNTRIES.map(c => <option key={c[0]} value={c[0]}>{c[0]} {c[1]}</option>)}
            </select>
            <input type="tel" id="inputPhone" placeholder="Phone number" autoComplete="tel-national" inputMode="tel"
              value={s.typed} onChange={e => this.setState({typed: e.target.value, error: ''})} required autoFocus />
          </div>
          {s.error ? <div className="babel-login-error" role="alert">{s.error}</div> : null}
          <div className="panel-form-row">
            <CheckBox id="save-token" name="save-token" checked={s.saveToken} onChange={this.handleToggleSaveToken} />
            <label htmlFor="save-token">&nbsp;Stay logged in</label>
          </div>
          <div className="dialog-buttons">
            <button className={'primary' + (s.busy ? ' disabled' : '')} type="submit" disabled={s.busy}>Send code</button>
          </div>
        </form>
      );
    }
    return (
      <form id="login-form" className="babel-login" onSubmit={e => e.preventDefault()}>
        <div className="babel-login-lead">Enter the 6-digit code for <b dir="ltr">{s.phone}</b>.</div>
        <input type="text" id="inputCode" className="babel-login-code" inputMode="numeric" pattern="[0-9]*" maxLength={6}
          autoComplete="one-time-code" placeholder="••••••" aria-label="Code" value={s.code}
          onChange={this.handleCodeChange} disabled={!!s.locked || this.props.disabled} autoFocus />
        {s.error ? <div className="babel-login-error" role="alert">{s.error}</div> : null}
        {s.info && !s.error ? <div className="babel-login-info">{s.info}</div> : null}
        <div className="babel-login-links">
          {s.resendLeft > 0 ?
            <span id="resend-wait" className="babel-login-wait">Resend code in {mmss(s.resendLeft)}</span> :
            <a href="#" id="resend" onClick={e => { e.preventDefault(); this.sendCode(null, true); }}>Resend code</a>}
          <a href="#" id="wrong-number" onClick={e => { e.preventDefault(); this.setState({step: 'phone', code: '', error: '', info: ''}); }}>Wrong number?</a>
        </div>
      </form>
    );
  }
};

// The classic username + password form only when asked for (development, the sample users): .../?classic=1
export default function LoginView(props) {
  return /[?&]classic=1/.test(window.location.search) ? <PasswordLoginView {...props} /> : <PhoneLoginView {...props} />;
}
